import * as React from "react";
import { Alert, Box, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { UPDATE_BUSINESS_BASIC } from "../../../../platform/graphql/mutations";
import {
  RELEASE_BUSINESS_CLAIM,
  SAVE_CARE_SETTINGS,
} from "../../../../platform/graphql/mutations/care";
import { COUNTRY_ID } from "../../../../platform/pages/customer/homeShared";
import {
  ACTIVE_STATUS,
  buildBusinessUpdateInput,
  INACTIVE_STATUS,
  normalizeText,
  PAGE_SIZE,
} from "./planyAdminDashboard/shared";
import { GET_PLATFORM_ADMIN_BUSINESSES } from "./planyAdminDashboard/query";
import { BusinessListPanel } from "./planyAdminDashboard/BusinessListPanel";
import { BusinessDetailsPanel } from "./planyAdminDashboard/BusinessDetailsPanel";

export default function PlanyAdminDashboardPage() {
  const { providerUser } = React.useContext(UserContext);
  const canViewDashboard =
    Number(providerUser?.userId || 0) > 0 && providerUser?.isPlatformAdmin === true;

  const [searchText, setSearchText] = React.useState("");
  const [claimFilter, setClaimFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [stateFilter, setStateFilter] = React.useState("");
  const [zipFilter, setZipFilter] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [selectedBusinessId, setSelectedBusinessId] = React.useState<number | null>(null);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [savingAction, setSavingAction] = React.useState("");
  const [localBusinesses, setLocalBusinesses] = React.useState<any[]>([]);
  const [stableTotalBusinesses, setStableTotalBusinesses] = React.useState(0);
  const filterSignature = React.useMemo(
    () =>
      JSON.stringify({
        searchText,
        claimFilter,
        statusFilter,
        stateFilter,
        zipFilter,
      }),
    [claimFilter, searchText, stateFilter, statusFilter, zipFilter],
  );
  const lastFilterSignatureRef = React.useRef<string | null>(null);

  const queryVariables = React.useMemo(
    () => ({
      countryId: COUNTRY_ID,
      stateCode: normalizeText(stateFilter).toUpperCase() || null,
      search: normalizeText(searchText) || null,
      zip: normalizeText(zipFilter) || null,
      status:
        statusFilter === "active"
          ? ACTIVE_STATUS
          : statusFilter === "inactive"
            ? INACTIVE_STATUS
            : null,
      claimed: claimFilter === "all" ? null : claimFilter,
      limit: PAGE_SIZE,
      offset: Math.max(0, (page - 1) * PAGE_SIZE),
    }),
    [claimFilter, page, searchText, stateFilter, statusFilter, zipFilter],
  );

  const businessesQuery = useQuery(GET_PLATFORM_ADMIN_BUSINESSES, {
    variables: queryVariables,
    skip: !canViewDashboard,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    returnPartialData: false,
  });
  const [updateBusiness] = useMutation(UPDATE_BUSINESS_BASIC);
  const [saveCareSettings] = useMutation(SAVE_CARE_SETTINGS);
  const [releaseBusinessClaim] = useMutation(RELEASE_BUSINESS_CLAIM);

  React.useEffect(() => {
    const queryItems = businessesQuery?.data?.adminBusinessesPaged?.items;
    setLocalBusinesses(Array.isArray(queryItems) ? queryItems : []);
  }, [businessesQuery?.data?.adminBusinessesPaged?.items]);

  React.useEffect(() => {
    const nextTotal = Number(businessesQuery?.data?.adminBusinessesPaged?.total);
    if (Number.isFinite(nextTotal) && nextTotal >= 0) {
      setStableTotalBusinesses(nextTotal);
    }
  }, [businessesQuery?.data?.adminBusinessesPaged?.total]);

  const businesses = localBusinesses;
  const totalBusinesses = stableTotalBusinesses;
  const pageCount = Math.max(1, Math.ceil(totalBusinesses / PAGE_SIZE));

  React.useEffect(() => {
    if (lastFilterSignatureRef.current === null) {
      lastFilterSignatureRef.current = filterSignature;
      return;
    }

    if (lastFilterSignatureRef.current !== filterSignature) {
      lastFilterSignatureRef.current = filterSignature;
      setPage(1);
    }
  }, [filterSignature]);

  React.useEffect(() => {
    if (!businessesQuery.loading && page > pageCount) {
      setPage(pageCount);
    }
  }, [businessesQuery.loading, page, pageCount]);

  const handlePageChange = React.useCallback(
    (nextPageInput: number) => {
      setPage((prev) => {
        const nextPage = Math.max(1, Math.min(pageCount, Number(nextPageInput) || 1));
        if (nextPage !== prev) {
          setLocalBusinesses([]);
        }
        return nextPage;
      });
    },
    [pageCount],
  );

  React.useEffect(() => {
    if (!businesses.length) {
      setSelectedBusinessId(null);
      return;
    }

    const stillVisible = businesses.some(
      (business: any) => Number(business?.id) === Number(selectedBusinessId),
    );
    if (!stillVisible) {
      setSelectedBusinessId(Number(businesses[0]?.id || 0));
    }
  }, [businesses, selectedBusinessId]);

  const selectedBusiness = React.useMemo(
    () =>
      businesses.find((business: any) => Number(business?.id) === Number(selectedBusinessId)) ||
      null,
    [businesses, selectedBusinessId],
  );

  const runBusinessUpdate = React.useCallback(
    async (business: any, patch: Record<string, any>, successText: string) => {
      setActionMessage(null);
      setActionError(null);
      setSavingAction(successText);

      try {
        await updateBusiness({
          variables: { input: buildBusinessUpdateInput(business, patch) },
        });

        setLocalBusinesses((prev) =>
          prev.map((item) =>
            Number(item?.id) === Number(business?.id) ? { ...item, ...patch } : item,
          ),
        );

        const refetchResult = await businessesQuery.refetch();
        const refreshedItems = refetchResult?.data?.adminBusinessesPaged?.items;
        if (Array.isArray(refreshedItems)) {
          setLocalBusinesses(refreshedItems);
        }

        setActionMessage(successText);
      } catch (error: any) {
        setActionError(
          error?.graphQLErrors?.[0]?.message ||
            error?.networkError?.message ||
            error?.message ||
            "Unable to update this business right now.",
        );
      } finally {
        setSavingAction("");
      }
    },
    [businessesQuery, updateBusiness],
  );

  const runCareDirectoryApproval = React.useCallback(
    async (business: any) => {
      const businessId = Number(business?.id || 0);
      if (!businessId) return;

      setActionMessage(null);
      setActionError(null);
      setSavingAction("Approved care listing and enabled directory visibility.");

      const now = new Date().toISOString();
      const careSettings = business?.careSettings || {};

      try {
        await saveCareSettings({
          variables: {
            input: {
              businessId,
              afhLicenseNumber: careSettings?.afhLicenseNumber || null,
              afhLicenseVerified: true,
              licenseStatus: careSettings?.licenseStatus || "ACTIVE",
              facilityStatusCode: careSettings?.facilityStatusCode || "OP",
              verificationSource:
                careSettings?.verificationSource || "PLANY365_ADMIN",
              verifiedAt: careSettings?.verifiedAt || now,
              lastCheckedAt: now,
            },
          },
        });

        await updateBusiness({
          variables: {
            input: buildBusinessUpdateInput(business, {
              displayOnBookingPage: true,
            }),
          },
        });

        setLocalBusinesses((prev) =>
          prev.map((item) =>
            Number(item?.id) === businessId
              ? {
                  ...item,
                  displayOnBookingPage: true,
                  careSettings: {
                    ...(item?.careSettings || {}),
                    ...(careSettings || {}),
                    afhLicenseVerified: true,
                    licenseStatus: careSettings?.licenseStatus || "ACTIVE",
                    facilityStatusCode: careSettings?.facilityStatusCode || "OP",
                    verificationSource:
                      careSettings?.verificationSource || "PLANY365_ADMIN",
                    verifiedAt: careSettings?.verifiedAt || now,
                    lastCheckedAt: now,
                  },
                }
              : item,
          ),
        );

        const refetchResult = await businessesQuery.refetch();
        const refreshedItems = refetchResult?.data?.adminBusinessesPaged?.items;
        if (Array.isArray(refreshedItems)) {
          setLocalBusinesses(refreshedItems);
        }

        setActionMessage(
          "Marked this business care-approved and visible on the Plany365 Care directory."
        );
      } catch (error: any) {
        setActionError(
          error?.graphQLErrors?.[0]?.message ||
            error?.networkError?.message ||
            error?.message ||
            "Unable to approve this care listing right now.",
        );
      } finally {
        setSavingAction("");
      }
    },
    [businessesQuery, saveCareSettings, updateBusiness],
  );

  const runBusinessClaimRelease = React.useCallback(
    async (business: any) => {
      const businessId = Number(business?.id || 0);
      if (!businessId) return;

      setActionMessage(null);
      setActionError(null);
      setSavingAction("Returned business to unclaimed.");

      try {
        const res = await releaseBusinessClaim({
          variables: { businessId },
        });
        const payload = res?.data?.releaseBusinessClaim;
        if (!payload?.success) {
          throw new Error(payload?.message || "Unable to revoke this business claim right now.");
        }

        setLocalBusinesses((prev) =>
          prev.map((item) =>
            Number(item?.id) === businessId
              ? {
                  ...item,
                  createdBy: null,
                  claimStatus: "UNCLAIMED",
                }
              : item,
          ),
        );

        const refetchResult = await businessesQuery.refetch();
        const refreshedItems = refetchResult?.data?.adminBusinessesPaged?.items;
        if (Array.isArray(refreshedItems)) {
          setLocalBusinesses(refreshedItems);
        }

        setActionMessage(payload?.message || "Business claim revoked.");
      } catch (error: any) {
        setActionError(
          error?.graphQLErrors?.[0]?.message ||
            error?.networkError?.message ||
            error?.message ||
            "Unable to revoke this business claim right now.",
        );
      } finally {
        setSavingAction("");
      }
    },
    [businessesQuery, releaseBusinessClaim],
  );

  const copyText = React.useCallback(async (value: string, label: string) => {
    const text = normalizeText(value);
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setActionError(null);
      setActionMessage(`${label} copied.`);
    } catch (error: any) {
      setActionError(error?.message || `Unable to copy ${label.toLowerCase()}.`);
    }
  }, []);

  if (!canViewDashboard) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", px: 2, py: 4 }}>
        <Alert severity="error">
          This dashboard is reserved for Plany365 system administrators.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1500, mx: "auto", px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 3 } }}>
      <Stack spacing={2.25}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
          <Stack spacing={1.25}>
            <Typography variant="overline" color="primary.main">
              Plany365 Internal
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              Business Admin Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 980 }}>
              Search and manage existing businesses from one place. This view is for quick
              communication, billing support, trial overrides, and public-status control.
            </Typography>
            <Alert severity="info">
              Trial controls here use the business-level override already supported by the
              platform. Business status changes can hide a business from active and public flows,
              so use them deliberately.
            </Alert>
          </Stack>
        </Paper>

        {businessesQuery.loading && <LinearProgress />}
        {actionMessage ? <Alert severity="success">{actionMessage}</Alert> : null}
        {actionError ? <Alert severity="error">{actionError}</Alert> : null}
        {businessesQuery.error ? (
          <Alert severity="error">Unable to load businesses right now.</Alert>
        ) : null}

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
          <BusinessListPanel
            businesses={businesses}
            claimFilter={claimFilter}
            onClaimFilterChange={setClaimFilter}
            onPageChange={handlePageChange}
            onSearchTextChange={setSearchText}
            onSelectedBusinessIdChange={setSelectedBusinessId}
            onStateFilterChange={setStateFilter}
            onStatusFilterChange={setStatusFilter}
            onZipFilterChange={setZipFilter}
            page={page}
            pageCount={pageCount}
            searchText={searchText}
            selectedBusinessId={selectedBusinessId}
            stateFilter={stateFilter}
            statusFilter={statusFilter}
            totalBusinesses={totalBusinesses}
            zipFilter={zipFilter}
          />

        <BusinessDetailsPanel
          copyText={copyText}
          runCareDirectoryApproval={runCareDirectoryApproval}
          runBusinessClaimRelease={runBusinessClaimRelease}
          runBusinessUpdate={runBusinessUpdate}
          savingAction={savingAction}
          selectedBusiness={selectedBusiness}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
